<template>
  <div class="repartition-geo-chart">
    <div class="chart-header">
      <span class="chart-title">Répartition géographique par antenne</span>
    </div>
    <div v-if="loading" class="chart-loading">
      <i class="el-icon-loading" />
      <p>Chargement des données...</p>
    </div>
    <div v-else-if="hasNoData" class="chart-no-data">
      <i class="el-icon-warning" />
      <p>Aucune donnée disponible pour les filtres sélectionnés</p>
    </div>
    <div v-else ref="chartContainer" class="chart-container">
      <Bar
        :chart-data="chartData"
        :chart-options="chartOptions"
        :height="chartHeight || null"
      />
    </div>
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { mapGetters } from 'vuex';
import DashboardAPI from '@/api/dashboard';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default {
  name: 'RepartitionGeoChart',
  components: {
    Bar,
  },
  data() {
    return {
      loading: false,
      kpiRequestsCompleted: true, // Par défaut true pour permettre le chargement initial
      chartData: {
        labels: [],
        datasets: [],
      },
      chartHeight: null,
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top',
          },
          title: {
            display: false,
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label(context) {
                const label = context.dataset.label || '';
                const value = context.parsed.y;
                if (label.includes('tonnes')) {
                  return `${label}: ${value.toFixed(2)} t`;
                }
                return `${label}: ${value.toFixed(2)} ha`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Quantité / Surface',
            },
          },
          x: {
            title: {
              display: true,
              text: 'Antennes',
            },
          },
        },
        interaction: {
          mode: 'index',
          intersect: false,
        },
      },
    };
  },
  computed: {
    ...mapGetters('dashboard', ['filters']),
    ...mapGetters('camp_agricole', ['camp_id']),
    hasNoData() {
      return !this.loading &&
        (!this.chartData.labels || this.chartData.labels.length === 0) &&
        (!this.chartData.datasets || this.chartData.datasets.length === 0 ||
          !this.chartData.datasets.some(d => d.data && d.data.length > 0));
    },
  },
  watch: {
    filters: {
      handler() {
        // Attendre que les requêtes KPI soient terminées avant de faire les requêtes des graphiques
        if (this.kpiRequestsCompleted) {
          this.fetchData();
        }
      },
      deep: true,
    },
    camp_id: {
      handler() {
        // Attendre que les requêtes KPI soient terminées avant de faire les requêtes des graphiques
        if (this.kpiRequestsCompleted) {
          this.fetchData();
        }
      },
    },
  },
  mounted() {
    // Écouter l'événement indiquant que les requêtes KPI sont terminées
    this.$root.$on('kpi-requests-completed', this.onKpiRequestsCompleted);
    // Écouter l'événement indiquant que les filtres sont appliqués (pour bloquer les requêtes)
    this.$root.$on('filters-applied', this.onFiltersApplied);
    // Écouter l'événement indiquant que les filtres commencent à être appliqués
    this.$root.$on('kpi-filters-loading', this.onKpiFiltersLoading);
    // Charger les données initiales
    this.fetchData();
    // Calculer la hauteur après que le DOM soit complètement rendu
    setTimeout(() => {
      this.calculateChartHeight();
    }, 100);
    window.addEventListener('resize', this.calculateChartHeight);
  },
  beforeDestroy() {
    this.$root.$off('kpi-requests-completed', this.onKpiRequestsCompleted);
    this.$root.$off('filters-applied', this.onFiltersApplied);
    this.$root.$off('kpi-filters-loading', this.onKpiFiltersLoading);
    window.removeEventListener('resize', this.calculateChartHeight);
  },
  methods: {
    onKpiRequestsCompleted() {
      this.kpiRequestsCompleted = true;
      // Faire les requêtes maintenant que les requêtes KPI sont terminées
      this.fetchData();
    },
    onFiltersApplied() {
      // Les filtres ont été appliqués, mais attendre que les requêtes KPI soient terminées
      // L'événement kpi-requests-completed sera émis après
    },
    onKpiFiltersLoading(data) {
      // Bloquer les requêtes des graphiques pendant que les requêtes KPI sont en cours
      if (data && data.loading) {
        this.kpiRequestsCompleted = false;
      }
    },
    buildApiFilters() {
      const activeFilters = this.filters || {};
      const niveau = activeFilters.niveau || {};

      return {
        campagne_id: this.camp_id || null,
        periode: {
          type: activeFilters.periode?.type || 'campagne',
          date_debut: activeFilters.periode?.date_debut || null,
          date_fin: activeFilters.periode?.date_fin || null,
        },
        ci_id: niveau.ci_id !== undefined && niveau.ci_id !== null ? niveau.ci_id : null,
        antenne_id: niveau.antenne_id !== undefined && niveau.antenne_id !== null ? niveau.antenne_id : null,
        groupement_id: niveau.groupement_id !== undefined && niveau.groupement_id !== null ? niveau.groupement_id : null,
        producteur_id: niveau.producteur_id !== undefined && niveau.producteur_id !== null ? niveau.producteur_id : null,
        cultures: (activeFilters.cultures || [])
          .map(id => {
            if (typeof id === 'number') {
              return id;
            }
            if (typeof id === 'object' && id !== null && id.id) {
              return typeof id.id === 'number' ? id.id : parseInt(id.id, 10);
            }
            if (typeof id === 'string') {
              const numId = parseInt(id, 10);
              return isNaN(numId) ? null : numId;
            }
            return id;
          })
          .filter(id => id !== null && id !== undefined && !isNaN(id)),
        certification: activeFilters.certification || 'toutes',
      };
    },
    async fetchData() {
      this.loading = true;
      try {
        const apiFilters = this.buildApiFilters();
        const response = await DashboardAPI.getRepartitionGeoChart(apiFilters);

        if (response && response.data) {
          this.chartData = {
            labels: response.data.labels || [],
            datasets: response.data.datasets || [],
          };
        }
      } catch (error) {
        console.error('[RepartitionGeoChart] Erreur lors du chargement des données:', error);
        console.error('[RepartitionGeoChart] Détails de l\'erreur:', error.response?.data || error.message);
        this.$message.error('Erreur lors du chargement des données de répartition géographique');
      } finally {
        this.loading = false;
        // Recalculer la hauteur après le chargement des données
        this.$nextTick(() => {
          setTimeout(() => {
            this.calculateChartHeight();
          }, 50);
        });
      }
    },
    calculateChartHeight() {
      this.$nextTick(() => {
        setTimeout(() => {
          const parent = this.$el;
          if (!parent) {
            return;
          }

          const header = parent.querySelector('.chart-header');
          const headerHeight = header ? (header.offsetHeight || header.clientHeight || 50) : 50;

          // Utiliser getBoundingClientRect pour une mesure plus précise
          const parentRect = parent.getBoundingClientRect();
          let parentHeight = parentRect.height;

          // Si getBoundingClientRect ne donne pas de résultat, essayer d'autres méthodes
          if (!parentHeight || parentHeight === 0) {
            parentHeight = parent.offsetHeight || parent.clientHeight;
          }

          // Si toujours pas de hauteur, utiliser la hauteur du parent (el-card)
          if (!parentHeight || parentHeight === 0) {
            const cardParent = parent.closest('.el-card__body') || parent.closest('.chart-card');
            if (cardParent) {
              const cardRect = cardParent.getBoundingClientRect();
              parentHeight = cardRect.height || cardParent.offsetHeight || cardParent.clientHeight;
            }
          }

          const availableHeight = parentHeight - headerHeight;

          if (availableHeight > 0) {
            this.chartHeight = Math.max(availableHeight, 400);
          } else if (this.$refs.chartContainer) {
            const containerRect = this.$refs.chartContainer.getBoundingClientRect();
            const containerHeight = containerRect.height || this.$refs.chartContainer.offsetHeight || this.$refs.chartContainer.clientHeight;
            if (containerHeight > 0) {
              this.chartHeight = Math.max(containerHeight, 400);
            } else {
              // Fallback: utiliser une hauteur par défaut basée sur min-height
              this.chartHeight = 400;
            }
          } else {
            // Fallback final
            this.chartHeight = 400;
          }
        }, 150);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.repartition-geo-chart {
  width: 100%;
  height: 100%;
  min-height: 500px;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  .chart-header {
    background-color: #00ab53;
    color: #fff;
    padding: 12px 16px;
    border-radius: 4px 4px 0 0;
    flex-shrink: 0;

    .chart-title {
      font-size: 16px;
      font-weight: bold;
      display: block;
    }
  }

  .chart-container {
    flex: 1 1 0;
    min-height: 400px;
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;

    ::v-deep > div {
      width: 100% !important;
      height: 100% !important;
      position: relative;
    }

    ::v-deep canvas {
      width: 100% !important;
      height: 100% !important;
      display: block;
    }

    ::v-deep .chartjs-render-monitor {
      width: 100% !important;
      height: 100% !important;
    }
  }

  .chart-loading {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    color: #909399;

    i {
      font-size: 32px;
      color: #00ab53;
      animation: rotate 1s linear infinite;
    }

    p {
      margin-top: 16px;
      font-size: 14px;
    }
  }

  .chart-no-data {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    color: #909399;

    i {
      font-size: 32px;
      color: #e6a23c;
      margin-bottom: 16px;
    }

    p {
      font-size: 14px;
      color: #606266;
    }
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>

